import { useSpring, animated } from '@react-spring/web';
import { Tooltip } from 'antd';
import cls from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState, useRef } from 'react';
import ReactCopyToClipboard from 'react-copy-to-clipboard';
import { useThrottledCallback } from 'use-debounce';

import styles from './index.module.scss';
import { getEditPageUrl, getFmCreationPageUrl, track } from '../../..';
import { collectedIcon, collectIcon, emptyPreviewIcon } from '../../../assets/ImageCreation';
import arrowUpIcon from '../../../assets/ImageCreation/arrow-up.svg';
import inpaintIcon from '../../../assets/ImageCreation/inpaint.svg';
import regenerateIcon from '../../../assets/ImageCreation/regenerate.svg';
import useImageIcon from '../../../assets/ImageCreation/use-image.svg';
import { copiedIcon, copyIcon } from '../../../assets/ImageLibrary';
import { useInpaintImgIdList } from '../../../hooks/useInpaintImgIdList';
import { useAppDispatch, useAppSelector } from '../../../state';
// import generateFailedImg from '../../../assets/ImageCreation/card-generate-error.svg';
import {
  cancelStarImage,
  addToPollQueue,
  IImgElemWithInpaint,
  ITaskResult,
  regeneratePreviewImage,
  setInpaintItem,
  setIsGenerating,
  starImage,
} from '../../../state/image';
import StepLoading from '../../Components/Loading/step';
import { OutlineBtn, PrimaryBtn } from '../../Components/PrimaryBtn';
import { LocalImage } from '../../Image';
import DetailModal from '../../Image/DetailModal';

function MockLoadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (Math.random() > 0.6) {
          return prev;
        }
        if (prev >= 99) {
          clearInterval(interval);
        }
        return Math.min(99, Math.ceil(prev + Math.random() * 10));
      });
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return `${progress}%`;
}

function AvatarItem({
  imgItem,
  index,
  taskIdx,
  handleInpaint,
  handleUseImage,
  source,
}: {
  imgItem: IImgElemWithInpaint;
  index: number;
  taskIdx: number;
  handleInpaint: (imgElemIdx: number, taskIdx: number) => void;
  handleUseImage: (imgItem: IImgElemWithInpaint) => void;
  source: string;
}) {
  const isLoading = !imgItem.img_url;
  const collected = !!imgItem.materialId;

  const dispatch = useAppDispatch();
  const toggleCollect = useThrottledCallback(
    () => {
      if (collected) {
        dispatch(cancelStarImage({ imgId: imgItem.img_id!, materialId: imgItem.materialId! }));
      } else {
        dispatch(starImage({ imgId: imgItem.img_id!, taskIdx }));
      }
      track.send('collect_image_click', source, {
        collect_image_click_type: collected ? 'cancel' : 'collect',
      });
    },
    500,
    { trailing: false }
  );

  const [showDetailModal, setShowDetailModal] = useState(false);
  const taskResult = useAppSelector((state) => state.image.taskResult[taskIdx]);
  const { t } = useTranslation();
  const imgElem = useAppSelector((state) => state.image.imgMap[imgItem.img_id!]);
  const hasInpaintHistory = useMemo(
    () => !!imgElem?.inpaintImgIdList && imgElem.inpaintImgIdList.length > 1,
    [imgElem]
  );

  const inpaintImgIdList = useInpaintImgIdList({ imgId: imgItem.img_id! });

  if (isLoading) {
    return (
      <div className={cls(styles.avatarCard, styles.loading)}>
        <div className={styles.loadingWrapper}>
          <StepLoading />
          <MockLoadingProgress />
        </div>
        <LocalImage className={styles.logo} src={emptyPreviewIcon} />
        {hasInpaintHistory && (
          <div className={styles.cover}>
            <div className={styles.bottom}>
              <OutlineBtn
                icon={inpaintIcon}
                text={t('inpaint')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleInpaint(index, taskIdx);
                }}
                width={'50%'}
                className={styles.inpaint}
              />
              <PrimaryBtn
                icon={useImageIcon}
                text={t('use_image')}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                width={'50%'}
                className={styles.useImage}
                disabled
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.avatarCard} onClick={() => setShowDetailModal(true)}>
        <img className={styles.avatar} src={imgItem.img_url} alt="avatar" />
        {hasInpaintHistory && (
          <div className={styles.inpaintHistoryIcon}>
            {t('inpaint')} {inpaintImgIdList!.length - 1}
          </div>
        )}
        {collected && (
          <div
            className={styles.star}
            onClick={(e) => {
              e.stopPropagation();
              toggleCollect();
            }}
          >
            <LocalImage src={collectedIcon} alt="star icon" />
          </div>
        )}
        <div className={styles.cover}>
          {!collected && (
            <div
              className={styles.star}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollect();
              }}
            >
              <LocalImage src={collectIcon} alt="star icon" />
            </div>
          )}
          <div className={styles.bottom}>
            <OutlineBtn
              icon={inpaintIcon}
              text={t('inpaint')}
              onClick={(e) => {
                e.stopPropagation();
                handleInpaint(index, taskIdx);
              }}
              width={'50%'}
              className={styles.inpaint}
            />
            <PrimaryBtn
              icon={useImageIcon}
              text={t('use_image')}
              onClick={(e) => {
                e.stopPropagation();
                handleUseImage(imgItem);
              }}
              width={'50%'}
              className={styles.useImage}
            />
          </div>
        </div>
      </div>
      <DetailModal
        disableRecreate
        avatarData={{
          avatar_img_url: imgItem.img_url,
          img_desc: taskResult?.imgConfig.prompt,
          web_preview_extra: taskResult?.imgConfig.web_preview_extra,
          avatar_style_id: taskResult?.imgConfig.avatar_style_id_list?.[0],
        }}
        visible={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onInpaint={() => {
          track.send('inpaint_image_click', 'image_generation_page', {
            view: 'image_detail_popup_wnd',
          });
          handleInpaint(index, taskIdx);
        }}
        onCreateNpc={() => {
          track.send('use_image_click', 'image_generation_page', {
            view: 'image_detail_popup_wnd',
          });
          handleUseImage(imgItem);
        }}
        source="image_generation_page"
        materialId={imgItem.materialId!}
      />
    </>
  );
}

const AvatarList = ({
  taskIdx,
  imgIdList,
  source,
}: {
  taskIdx: number;
  imgIdList: number[];
  source: string;
}) => {
  const { imgMap } = useAppSelector((state) => state.image);

  const imgList = useMemo(() => imgIdList.map((imgId) => imgMap[imgId]), [imgIdList, imgMap]);

  const router = useRouter();
  const handleUseImage = useThrottledCallback(
    async (imgItem: IImgElemWithInpaint) => {
      let materialId = imgItem.materialId;
      // 如果是素材就带materialId，不是素材有 imgUrl 就行
      if (!materialId) {
        const res = await dispatch(starImage({ imgId: imgItem.img_id!, taskIdx }));
        if (starImage.fulfilled.match(res)) {
          materialId = res.payload.materialId;
        }
      }
      if (router.query.from === 'fm_creation') {
        window.open(
          getFmCreationPageUrl({
            materialImgUrl: imgItem.img_url,
            materialId,
            fmDraftId: router.query.fm_draft_id as string,
          }),
          '_blank'
        );
        return;
      }
      window.open(
        getEditPageUrl({
          materialImgUrl: imgItem.img_url,
          materialId,
          npcId: router.query.npc_id as string,
        }),
        '_blank'
      );
    },
    500,
    { trailing: false }
  );

  const dispatch = useAppDispatch();
  const handleInpaint = (imgElemIdx: number, taskIdx: number) => {
    // 获取当前图片
    const currentImgId = imgIdList[imgElemIdx];
    const currentImg = imgMap[currentImgId];

    dispatch(setInpaintItem({ imgElemIdx, taskIdx, show: true }));
    track.send('inpaint_image_click', source, {
      material_id: currentImg.materialId,
    });
  };

  return (
    <div className={styles.avatarList}>
      {imgList
        .map((img, index) => ({ img, index }))
        .filter(({ img }) => !img.isSensitive && !img.failed)
        .map(({ img, index }) => (
          <AvatarItem
            key={img.img_id}
            imgItem={img}
            index={index}
            taskIdx={taskIdx}
            handleInpaint={handleInpaint}
            handleUseImage={handleUseImage}
            source={source}
          />
        ))}
    </div>
  );
};

function CopyPrompt({ item }: { item: ITaskResult }) {
  const [copied, setCopied] = useState(false);
  return (
    <ReactCopyToClipboard
      text={item.imgConfig.prompt!}
      onCopy={() => {
        setCopied(true);
      }}
    >
      <div className="flex items-start cursor-pointer">
        <p className={cls(styles.tip, 'max-w-[560px]')}>{item.imgConfig.prompt}</p>
        <LocalImage className={styles.copyIcon} src={copied ? copiedIcon : copyIcon} alt="Copy" />
      </div>
    </ReactCopyToClipboard>
  );
}

function TaskResultItem({
  taskIdx,
  item,
  source,
}: {
  taskIdx: number;
  item: ITaskResult;
  source: string;
}) {
  const { isGenerating } = useAppSelector((state) => state.image);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const style = useSpring({
    from: { opacity: 0, maxHeight: 0, scaleY: 1 },
    to: { opacity: 1, maxHeight: 2999, scaleY: 1 },
    config: { duration: 300 },
  });

  const onRegenerate = () => {
    if (isGenerating) return;
    dispatch(setIsGenerating(true));
    dispatch(regeneratePreviewImage(taskIdx)).then((action) => {
      if (regeneratePreviewImage.fulfilled.match(action)) {
        const imgIds = action.payload.imgList
          ?.map((img: any) => img.img_id)
          .filter(Boolean) as number[];
        console.log('imgIds', imgIds);
        dispatch(addToPollQueue(imgIds));
      }
    });
  };

  // 自动滚动到视图中
  const taskRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const task = taskRef.current;
    if (task) {
      task.scrollIntoView({ behavior: 'smooth' });
    }
  }, [taskIdx]);

  return (
    <animated.div style={style} className={styles.task} ref={taskRef}>
      <div className={styles.taskHeader}>
        <Tooltip
          color="#1C1D20"
          title={<CopyPrompt item={item} />}
          placement="bottomLeft"
          arrow={false}
        >
          <p className={styles.prompt}>{item.imgConfig.prompt}</p>
        </Tooltip>

        <button
          className={cls(styles.regenerate, isGenerating && styles.disabled)}
          onClick={onRegenerate}
        >
          <img src={regenerateIcon.src} alt="regenerate icon" />
          {t('regenerate')}
        </button>
      </div>
      <AvatarList taskIdx={taskIdx} imgIdList={item.imgIdList} source={source} />
    </animated.div>
  );
}

interface PreviewListProps {
  source: string;
}

export const PreviewList: React.FC<PreviewListProps> = ({ source }) => {
  const { taskResult } = useAppSelector((state) => state.image);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const previewListRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const isEmpty = useMemo(() => taskResult.length === 0, [taskResult]);
  const reverseTaskResult = useMemo(
    () => [...taskResult].map((item, index) => ({ ...item, taskIdx: index })).reverse(),
    [taskResult]
  );

  useEffect(() => {
    const handleScroll = () => {
      if (previewListRef.current) {
        const { scrollTop, clientHeight } = previewListRef.current;
        setShowScrollToTop(scrollTop > clientHeight);
      }
    };

    const previewListElement = previewListRef.current;
    if (previewListElement) {
      previewListElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (previewListElement) {
        previewListElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [previewListRef]);

  const scrollToTop = () => {
    previewListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return isEmpty ? (
    <div className={styles.empty}>
      <LocalImage src={emptyPreviewIcon} />
      <p className={styles.emptyTitle}>{t('image_preview_empty_title')}</p>
      <p className={styles.emptyDesc}>{t('image_preview_empty_desc')}</p>
    </div>
  ) : (
    <div className={styles.previewList} ref={previewListRef}>
      {reverseTaskResult.map((item) => (
        <TaskResultItem key={item.taskIdx} taskIdx={item.taskIdx} item={item} source={source} />
      ))}
      {showScrollToTop && (
        <button className={styles.scrollToTopButton} onClick={scrollToTop}>
          <img src={arrowUpIcon.src} alt="arrow up icon" />
        </button>
      )}
    </div>
  );
};
